import { GetCheckInModalDataResponse } from '@/backend/features/missions/shared/schemas/getCheckInModalDataSchema';
import badgeProfilePlaceholder from '@/ui/assets/images/badgeProfilePlaceholder.svg';
import { BadgeCheckInModalInfoLine } from '@/ui/features/missions/components/BadgeCheckInModalInfoLine';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { accountEntityName, getAddressMultiLineString } from '@juvare/contact-management/backend/features/accounts/shared';
import { useAttachmentDataUrl } from '@juvare/platform-sdk-react/ui/hooks';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BadgeCheckInModalUserBlockProps {
    data: GetCheckInModalDataResponse;
}

export const BadgeCheckInModalUserBlock: React.FC<BadgeCheckInModalUserBlockProps> = ({ data }) => {
    const { t } = useTranslation('rms');

    const { data: attachmentUrl } = useAttachmentDataUrl(accountEntityName, data?.accountId, 'userProfilePicture');

    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                gap: 2,
                backgroundColor: themeColors.surface.secondary,
                p: 2,
                maxWidth: 556,
                width: '100%',
                [theme.breakpoints.down('md')]: { maxWidth: '100%' },
            })}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 164 }}>
                <Typography variant='overline' sx={{ color: themeColors.text.subtle }}>
                    {t('pages.home.checkInModal.profile')}
                </Typography>

                <Box sx={{ position: 'relative' }}>
                    <img
                        src={attachmentUrl || badgeProfilePlaceholder}
                        alt={`User profile picture${!attachmentUrl && ' placeholder'}`}
                        width='164'
                        height='218'
                        style={{ objectFit: 'cover', marginBottom: 8 }}
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3, gap: 2 }}>
                <Typography variant='h6' sx={{ wordBreak: 'break-word' }}>
                    {data.accountDisplayName}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <BadgeCheckInModalInfoLine
                        name={t('pages.home.checkInModal.accountInfo.organization')}
                        data={data.accountBadgeOrganization}
                    />

                    <BadgeCheckInModalInfoLine
                        name={t('pages.home.checkInModal.accountInfo.occupation')}
                        data={data.accountBadgeOccupation}
                    />

                    <BadgeCheckInModalInfoLine name={t('pages.home.checkInModal.accountInfo.gender')} data={data.gender} />

                    <BadgeCheckInModalInfoLine
                        name={t('pages.home.checkInModal.accountInfo.dateOfBirth')}
                        data={data.dateOfBirth ? t('date', { value: new Date(data.dateOfBirth) }) : undefined}
                    />

                    <BadgeCheckInModalInfoLine
                        name={t('pages.home.checkInModal.accountInfo.expires')}
                        data={data.accountBadgeExpirationDate ? t('date', { value: data.accountBadgeExpirationDate }) : undefined}
                    />

                    <BadgeCheckInModalInfoLine
                        name={t('pages.home.checkInModal.accountInfo.address')}
                        data={getAddressMultiLineString({
                            addressLine1: data.address?.addressLine1,
                            addressLine2: data.address?.addressLine2,
                            city: data.address?.city,
                            state: data.address?.state,
                            postalCode: data.address?.postalCode,
                        })}
                    />
                </Box>
            </Box>
        </Box>
    );
};
